import { useEffect, useState } from "react";
import toast from "../../../utils/toast";
import {
  getSocietiesList,
  getSocietyAmenitiesList,
  getSocietyListById,
  updateSocietyListById,
  createSociety,
  toggleSocietyStatusById,
} from "../../../services/propertyOwner/propertyOwnerServices";
import { getMasterPincode } from "../../../services/admin/adminServices";

const INITIAL_ADD_FORM = {
  name: "",
  pincode: "",
  area: "",
  district: "",
  stateId: 0,
  stateName: "",
  electricityRate: "",
  totalUnits: "",
  location: "",
  amenityIds: [],
  notes: "",
  isActive: true,
};

const getEmbedUrl = (location) => {
  if (!location || location.trim().length < 5) return null;

  // Try to extract lat,lng from any Google Maps URL format
  const coordMatch = location.match(/(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/);
  if (coordMatch) {
    const lat = coordMatch[1];
    const lng = coordMatch[2];
    return `https://www.google.com/maps?q=${lat},${lng}&output=embed`;
  }

  // Plain text / place name — use as search query
  if (!location.startsWith("http")) {
    return `https://www.google.com/maps?q=${encodeURIComponent(location.trim())}&output=embed`;
  }

  return null;
};

export default function Buildings() {
  const [isOpenAddSocietyModal, setIsOpenAddSocietyModal] = useState(false);
  const [isOpenEditSocietyModal, setIsOpenEditSocietyModal] = useState(false);
  const [isSocietyData, setSocietyData] = useState(null);
  const [isSocietyAmenitiesData, setSocietyAmenitiesData] = useState([]);
  const [selectedSociety, setSelectedSociety] = useState(null);

  const [addForm, setAddForm] = useState(INITIAL_ADD_FORM);
  const [addErrors, setAddErrors] = useState({});
  const [addLoading, setAddLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);

  const [editForm, setEditForm] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    pageNumber: 1,
    pageSize: 10,
  });

  useEffect(() => {
    fetchSocietyList();
  }, [filters]);

  useEffect(() => {
    if (isOpenAddSocietyModal || isOpenEditSocietyModal) {
      fetchSocietyAmenitiesList();
    }
  }, [isOpenAddSocietyModal, isOpenEditSocietyModal]);

  const fetchSocietyList = async () => {
    setListLoading(true);
    try {
      // await new Promise((resolve) => setTimeout(resolve, 5000));
      const res = await getSocietiesList(filters);
      if (res.statusCode === 1) {
        setSocietyData(res);
        setTotalCount(res.data.paging.totalCount);
        setTotalPages(Math.ceil(res.data.paging.pageSize));
      }
    } catch (err) {
      console.log(err.response?.data?.message || "Failed to fetch societies");
    } finally {
      setListLoading(false);
    }
  };

  const fetchSocietyAmenitiesList = async () => {
    try {
      const res = await getSocietyAmenitiesList();
      if (res.statusCode === 1)
        setSocietyAmenitiesData(res.data.amenities || []);
    } catch (err) {
      console.log(err.response?.data?.message || "Failed to fetch amenities");
    }
  };
  // ── Add handlers ──
  const handleAddChange = (e) => {
    const { name, value } = e.target;
    setAddForm((prev) => ({ ...prev, [name]: value }));
    setAddErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePageChange = (page) => {
    setFilters((prev) => ({ ...prev, pageNumber: page }));
  };

  const handleAddPincodeChange = async (e) => {
    const val = e.target.value;
    handleAddChange(e);
    if (val.length === 6) {
      try {
        const res = await getMasterPincode(val);
        if (res.statusCode === 1) {
          setAddForm((prev) => ({
            ...prev,
            district: res.data.district,
            stateId: Number(res.data.stateId),
            stateName: res.data.stateName,
            area: res.data.areaName || prev.area,
          }));
        }
      } catch (err) {
        console.log("Pincode not found");
      }
    } else {
      setAddForm((prev) => ({ ...prev, district: "", stateId: 0 }));
    }
  };

  const handleAddAmenityToggle = (amenityId) => {
    setAddForm((prev) => ({
      ...prev,
      amenityIds: prev.amenityIds.includes(amenityId)
        ? prev.amenityIds.filter((id) => id !== amenityId)
        : [...prev.amenityIds, amenityId],
    }));
  };

  const openAddModal = () => {
    setAddForm(INITIAL_ADD_FORM);
    setAddErrors({});
    setIsOpenAddSocietyModal(true);
  };

  const closeAddModal = () => {
    setIsOpenAddSocietyModal(false);
    setAddForm(INITIAL_ADD_FORM);
    setAddErrors({});
  };

  const validateAdd = () => {
    const e = {};
    if (!addForm.name) e.name = "Society name is required.";
    if (!addForm.pincode) e.pincode = "Pincode is required.";
    setAddErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleAddSociety = async () => {
    if (!validateAdd()) return;
    setAddLoading(true);
    try {
      const payload = {
        name: addForm.name,
        pincode: addForm.pincode,
        area: addForm.area,
        district: addForm.district,
        stateId: Number(addForm.stateId),
        electricityRate: Number(addForm.electricityRate) || 0,
        totalUnits: Number(addForm.totalUnits) || 0,
        location: addForm.location,
        notes: addForm.notes,
        isActive: addForm.isActive,
        amenityIds: addForm.amenityIds.length > 0 ? addForm.amenityIds : [0],
      };
      const res = await createSociety(payload);
      if (res.statusCode === 1) {
        toast.success(res.message || "Society created successfully.");
        closeAddModal();
        fetchSocietyList();
      } else {
        toast.error(res.message || "Failed to create society.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setAddLoading(false);
    }
  };

  // ── Edit handlers ──
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
    setEditErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleEditPincodeChange = async (e) => {
    const val = e.target.value;
    handleEditChange(e);
    if (val.length === 6) {
      try {
        const res = await getMasterPincode(val);
        if (res.statusCode === 1) {
          setEditForm((prev) => ({
            ...prev,
            district: res.data.district,
            stateId: Number(res.data.stateId),
            stateName: res.data.stateName,
            area: res.data.areaName || prev.area,
          }));
        }
      } catch (err) {
        console.log("Pincode not found");
      }
    } else {
      setEditForm((prev) => ({ ...prev, district: "", stateId: 0 }));
    }
  };

  const handleEditAmenityToggle = (amenityId) => {
    setEditForm((prev) => ({
      ...prev,
      amenityIds: prev.amenityIds.includes(amenityId)
        ? prev.amenityIds.filter((id) => id !== amenityId)
        : [...prev.amenityIds, amenityId],
    }));
  };

  const openEditModal = async (societyId) => {
    try {
      const res = await getSocietyListById(societyId);
      if (res.statusCode === 1) {
        const d = res.data;
        setSelectedSociety(d);
        setEditForm({
          name: d.name || "",
          pincode: d.pincode || "",
          area: d.area || "",
          district: d.district || "",
          stateId: Number(d.stateId) || 0,
          stateName: d.stateName || "",
          electricityRate: d.electricityRate || "",
          totalUnits: d.totalUnits || "",
          location: d.location || "",
          amenityIds: d.amenityIds ? d.amenityIds.split(",").map(Number) : [],
          notes: d.notes || "",
          isActive: d.isActive,
        });
        setEditErrors({});
        setIsOpenEditSocietyModal(true);
      }
    } catch (err) {
      toast.error("Failed to load society details.");
    }
  };

  const closeEditModal = () => {
    setIsOpenEditSocietyModal(false);
    setSelectedSociety(null);
    setEditForm({});
    setEditErrors({});
  };

  const validateEdit = () => {
    const e = {};
    if (!editForm.name) e.name = "Society name is required.";
    if (!editForm.pincode) e.pincode = "Pincode is required.";
    setEditErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleUpdateSociety = async () => {
    if (!validateEdit()) return;
    setEditLoading(true);
    try {
      const payload = {
        name: editForm.name,
        pincode: editForm.pincode,
        area: editForm.area,
        district: editForm.district,
        stateId: Number(editForm.stateId),
        electricityRate: Number(editForm.electricityRate) || 0,
        totalUnits: Number(editForm.totalUnits) || 0,
        location: editForm.location,
        notes: editForm.notes,
        isActive: editForm.isActive,
        amenityIds: editForm.amenityIds.length > 0 ? editForm.amenityIds : [0],
      };
      const res = await updateSocietyListById(
        selectedSociety.societyId,
        payload,
      );
      if (res.statusCode === 1) {
        toast.success(res.message || "Society updated successfully.");
        closeEditModal();
        fetchSocietyList();
      } else {
        toast.error(res.message || "Failed to update society.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setEditLoading(false);
    }
  };

  const handleToggleStatus = async (id, status) => {
    try {
      const payload = {
        isActive: !status,
      };
      const res = await toggleSocietyStatusById(id, payload);
      if (res.statusCode === 1) {
        toast.success(res.message);
        fetchSocietyList();
      }
    } catch (err) {
      toast.error("Failed to toggle  status.");
    }
  };

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title">Properties</div>
            <div className="page-sub">
              Manage your societies &amp; properties
            </div>
          </div>
          <div className="topbar-right">
            <button className="btn btn-primary" onClick={openAddModal}>
              + Add Society
            </button>
          </div>
        </div>

        <div className="content" id="content-area">
          <div className="stat-grid mb16">
            <div className="stat-card">
              <div className="stat-icon">🏢</div>
              <div className="stat-label">Total Societies</div>
              <div className="stat-value">
                {isSocietyData?.data.totalSocieties || 0}
              </div>
              <div className="stat-delta delta-neu">Registered</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🏠</div>
              <div className="stat-label">Total Units</div>
              <div className="stat-value">
                {isSocietyData?.data.totalUnits || 0}
              </div>
              <div className="stat-delta delta-up">Across all</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-label">Occupied</div>
              <div className="stat-value" style={{ color: "var(--green)" }}>
                {isSocietyData?.data.occupiedUnits || 0}
              </div>
              <div className="stat-delta delta-up">Across all</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🔓</div>
              <div className="stat-label">Vacant</div>
              <div className="stat-value" style={{ color: "var(--amber)" }}>
                {isSocietyData?.data.vacantUnits || 0}
              </div>
              <div className="stat-delta delta-dn">Across all</div>
            </div>
          </div>

          <div className="card">
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Society Name</th>
                    <th>Pincode</th>
                    <th>State</th>
                    <th>Elec Rate</th>
                    <th>Amenities</th>
                    <th>Occupancy</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {listLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 9 }).map((_, j) => (
                          <td key={j}>
                            <div
                              style={{
                                height: 14,
                                borderRadius: 6,
                                background:
                                  "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                                backgroundSize: "200% 100%",
                                animation: "shimmer 1.4s infinite",
                                width:
                                  j === 1 ? "80%" : j === 8 ? "60%" : "70%",
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : isSocietyData?.data?.societies?.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        style={{
                          textAlign: "center",
                          color: "var(--text-tertiary)",
                          padding: 24,
                        }}
                      >
                        No societies found.
                      </td>
                    </tr>
                  ) : (
                    isSocietyData?.data?.societies?.map((society, index) => (
                      <tr key={society.societyId}>
                        <td className="sn-col">
                          {" "}
                          {(filters.pageNumber - 1) * filters.pageSize +
                            index +
                            1}
                        </td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{society.name}</div>
                          <div
                            style={{
                              fontSize: 10,
                              color: "var(--text-tertiary)",
                            }}
                          >
                            {[society.area, society.district]
                              .filter(Boolean)
                              .join(", ") || "—"}
                          </div>
                        </td>
                        <td>{society.pincode || "—"}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {society.stateName || "—"}
                        </td>
                        <td>
                          {society.electricityRate
                            ? `₹${society.electricityRate}/unit`
                            : "—"}
                        </td>
                        <td
                          title={society?.amenityNames
                            ?.split(",")
                            .map((a) => a.trim())
                            .join(" | ")}
                        >
                          {society?.amenityNames ? (
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 3,
                              }}
                            >
                              {society.amenityNames
                                .split(",")
                                .slice(0, 3)
                                .map((a, i) => (
                                  <span
                                    key={i}
                                    style={{
                                      background: "var(--blue-light)",
                                      color: "#0C447C",
                                      borderRadius: 99,
                                      padding: "1px 7px",
                                      fontSize: 10,
                                    }}
                                  >
                                    {a.trim()}
                                  </span>
                                ))}
                              {society.amenityNames.split(",").length > 3 && (
                                <span
                                  style={{
                                    fontSize: 10,
                                    color: "var(--text-tertiary)",
                                  }}
                                >
                                  +{society.amenityNames.split(",").length - 3}
                                </span>
                              )}
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          <span
                            style={{
                              cursor: "pointer",
                              fontSize: 12,
                              fontWeight: 600,
                              color: "var(--green)",
                            }}
                          >
                            {society.occupiedUnits} Occupied
                          </span>
                          &nbsp;/&nbsp;
                          <span
                            style={{
                              cursor: "pointer",
                              fontSize: 12,
                              fontWeight: 600,
                              color: "var(--amber)",
                            }}
                          >
                            {society.vacantUnits} Vacant
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${society.isActive ? "badge-active" : "badge-inactive"}`}
                          >
                            {society.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <div className="td-actions">
                            <span
                              className="action-icon"
                              title="Edit"
                              onClick={() => openEditModal(society.societyId)}
                            >
                              <span className="tip">Edit</span>✎
                            </span>

                            {society.isActive ? (
                              <span
                                className="action-icon red"
                                title="Deactivate"
                                onClick={() =>
                                  handleToggleStatus(
                                    society.societyId,
                                    society.isActive,
                                  )
                                }
                              >
                                <span className="tip">Deactivate</span>⊗
                              </span>
                            ) : (
                              <span
                                className="action-icon green"
                                title="Activate"
                                onClick={() =>
                                  handleToggleStatus(
                                    society.societyId,
                                    society.isActive,
                                  )
                                }
                              >
                                <span className="tip">Activate</span>✓
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div
            style={{
              marginTop: 12,
              display: "flex",
              justifyContent: "space-between",
              fontSize: 12,
              color: "var(--text-secondary)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <select
                value={filters.pageSize}
                onChange={(e) =>
                  setFilters({
                    pageNumber: 1,
                    pageSize: Number(e.target.value),
                  })
                }
                style={{ fontSize: 12, padding: "4px 8px" }}
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
              </select>
              <span>
                Showing{" "}
                <strong>{isSocietyData?.data?.societies?.length ?? 0}</strong>{" "}
                of <strong>{totalCount}</strong>
              </span>
            </div>

            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <button
                className="btn btn-xs"
                disabled={filters.pageNumber === 1}
                onClick={() => handlePageChange(filters.pageNumber - 1)}
              >
                ‹ Prev
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <span
                    key={page}
                    onClick={() => handlePageChange(page)}
                    style={{
                      padding: "2px 8px",
                      background:
                        filters.pageNumber === page
                          ? "var(--blue)"
                          : "transparent",
                      color:
                        filters.pageNumber === page
                          ? "#fff"
                          : "var(--text-secondary)",
                      borderRadius: 5,
                      fontSize: 11,
                      cursor: "pointer",
                      border:
                        filters.pageNumber === page
                          ? "none"
                          : "1px solid var(--border-mid)",
                    }}
                  >
                    {page}
                  </span>
                ),
              )}

              <button
                className="btn btn-xs"
                disabled={filters.pageNumber === totalPages}
                onClick={() => handlePageChange(filters.pageNumber + 1)}
              >
                Next ›
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Add Society Modal ── */}
      {isOpenAddSocietyModal && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 820, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div className="modal-title">Add New Society</div>
              <button className="modal-close" onClick={closeAddModal}>
                ✕
              </button>
            </div>
            <div
              className="modal-body"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 20,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                    letterSpacing: ".06em",
                    marginBottom: 12,
                  }}
                >
                  Basic Details
                </div>

                <div className="form-group mb12">
                  <label>Society / Building Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={addForm.name}
                    onChange={handleAddChange}
                    placeholder="e.g. Sunrise PG Block A"
                    style={addErrors.name ? { borderColor: "#dc2626" } : {}}
                  />
                  {addErrors.name && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {addErrors.name}
                    </span>
                  )}
                </div>

                <div className="form-group mb12">
                  <label>Pincode *</label>
                  <input
                    type="text"
                    name="pincode"
                    value={addForm.pincode}
                    maxLength={6}
                    onChange={handleAddPincodeChange}
                    placeholder="6-digit pincode"
                    style={addErrors.pincode ? { borderColor: "#dc2626" } : {}}
                  />
                  {addErrors.pincode && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {addErrors.pincode}
                    </span>
                  )}
                </div>

                <div className="form-group mb12">
                  <label>Area / Locality</label>
                  <input
                    type="text"
                    name="area"
                    value={addForm.area}
                    onChange={handleAddChange}
                    placeholder="Auto-filled from pincode"
                  />
                </div>

                <div className="form-grid">
                  <div className="form-group mb12">
                    <label>District</label>
                    <input
                      type="text"
                      name="district"
                      value={addForm.district}
                      onChange={handleAddChange}
                      placeholder="Auto-filled from pincode"
                    />
                  </div>
                  <div className="form-group mb12">
                    <label>State</label>
                    <input
                      type="text"
                      name="stateName"
                      value={addForm.stateName}
                      onChange={handleAddChange}
                      placeholder="Auto-filled from pincode or type manually"
                    />
                  </div>
                </div>

                <div className="form-group mb12">
                  <label>⚡ Electricity Rate (₹ per unit)</label>
                  <input
                    type="number"
                    name="electricityRate"
                    value={addForm.electricityRate}
                    onChange={handleAddChange}
                    placeholder="e.g. 8.50"
                    step="0.01"
                    min={0}
                  />
                  <div className="input-hint">
                    Default for all units (can be overridden per unit)
                  </div>
                </div>

                <div className="form-group mb12">
                  <label>Total Units</label>
                  <input
                    type="number"
                    name="totalUnits"
                    value={addForm.totalUnits}
                    onChange={handleAddChange}
                    placeholder="e.g. 10"
                    min={0}
                  />
                </div>

                <div className="form-group mb12">
                  <label>📍 Location (Google Map Location)</label>
                  <input
                    type="text"
                    name="location"
                    value={addForm.location}
                    onChange={handleAddChange}
                    placeholder="Paste Google Maps link"
                  />
                </div>

                <div className="form-group mb12">
                  <label>Notes / Instructions</label>
                  <textarea
                    name="notes"
                    value={addForm.notes}
                    onChange={handleAddChange}
                    style={{ minHeight: 70 }}
                    placeholder="General notes for tenants…"
                  />
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    name="isActive"
                    value={addForm.isActive}
                    onChange={(e) =>
                      setAddForm((prev) => ({
                        ...prev,
                        isActive: e.target.value === "true",
                      }))
                    }
                  >
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </select>
                </div>
              </div>

              {/* Right — Amenities */}
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                    letterSpacing: ".06em",
                    marginBottom: 10,
                  }}
                >
                  Amenities
                </div>
                {/* <div
                  style={{ maxHeight: 480, overflowY: "auto", paddingRight: 4 }}
                > */}
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  {isSocietyAmenitiesData.map((amenity) => (
                    <label
                      key={amenity.amenityId}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                        fontSize: 12,
                        cursor: "pointer",
                        padding: "6px 10px",
                        borderRadius: "var(--radius-md)",
                        background: addForm.amenityIds.includes(
                          amenity.amenityId,
                        )
                          ? "var(--blue-light)"
                          : "var(--bg-primary)",
                        userSelect: "none",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={addForm.amenityIds.includes(amenity.amenityId)}
                        onChange={() =>
                          handleAddAmenityToggle(amenity.amenityId)
                        }
                        style={{ marginTop: 2, flexShrink: 0 }}
                      />
                      <span style={{ fontSize: 16, flexShrink: 0 }}>
                        {amenity.icon}
                      </span>
                      <span>
                        <span style={{ fontWeight: 600 }}>
                          {amenity.amenityName}
                        </span>
                        {amenity.description && amenity.showMode !== "name" && (
                          <div
                            style={{
                              fontSize: 10,
                              color: "var(--text-tertiary)",
                              marginTop: 1,
                              lineHeight: "1.4",
                            }}
                          >
                            {amenity.description}
                          </div>
                        )}
                      </span>
                    </label>
                  ))}
                  {/* </div> */}
                </div>

                {/* Map preview */}
                {(() => {
                  const embedUrl = getEmbedUrl(addForm.location);
                  return embedUrl ? (
                    <div
                      style={{
                        marginTop: 8,
                        borderRadius: "var(--radius-md)",
                        overflow: "hidden",
                        border: "0.5px solid var(--border-mid)",
                        height: 200,
                      }}
                    >
                      <iframe
                        src={embedUrl}
                        width="100%"
                        height="200"
                        style={{ border: 0, display: "block" }}
                        allowFullScreen
                        loading="lazy"
                        title="Map Preview"
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        marginTop: 8,
                        borderRadius: "var(--radius-md)",
                        background: "var(--bg-secondary)",
                        height: 100,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "var(--text-tertiary)",
                        fontSize: 12,
                        border: "0.5px solid var(--border-mid)",
                      }}
                    >
                      🗺️ Paste a Google Maps link above to preview map here
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeAddModal}
                disabled={addLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddSociety}
                disabled={addLoading}
              >
                {addLoading ? "Saving..." : "Save Society"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Society Modal ── */}
      {isOpenEditSocietyModal && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 820, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div className="modal-title">
                Edit Society — {selectedSociety?.name}
              </div>
              <button className="modal-close" onClick={closeEditModal}>
                ✕
              </button>
            </div>
            <div
              className="modal-body"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 20,
              }}
            >
              {/* Left — Basic Details */}
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                    letterSpacing: ".06em",
                    marginBottom: 12,
                  }}
                >
                  Basic Details
                </div>

                <div className="form-group mb12">
                  <label>Society / Building Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={editForm.name}
                    onChange={handleEditChange}
                    placeholder="e.g. Sunrise PG Block A"
                    style={editErrors.name ? { borderColor: "#dc2626" } : {}}
                  />
                  {editErrors.name && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {editErrors.name}
                    </span>
                  )}
                </div>

                <div className="form-group mb12">
                  <label>Pincode *</label>
                  <input
                    type="text"
                    name="pincode"
                    value={editForm.pincode}
                    maxLength={6}
                    onChange={handleEditPincodeChange}
                    placeholder="6-digit pincode"
                    style={editErrors.pincode ? { borderColor: "#dc2626" } : {}}
                  />
                  {editErrors.pincode && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {editErrors.pincode}
                    </span>
                  )}
                </div>

                <div className="form-group mb12">
                  <label>Area / Locality</label>
                  <input
                    type="text"
                    name="area"
                    value={editForm.area}
                    onChange={handleEditChange}
                    placeholder="Area or locality"
                  />
                </div>

                <div className="form-grid">
                  <div className="form-group mb12">
                    <label>District</label>
                    <input
                      type="text"
                      name="district"
                      value={editForm.district}
                      onChange={handleEditChange}
                      placeholder="Auto-filled from pincode"
                    />
                  </div>
                  <div className="form-group mb12">
                    <label>State</label>
                    <input
                      type="text"
                      name="stateName"
                      value={editForm.stateName || ""}
                      onChange={handleEditChange}
                      placeholder="Auto-filled from pincode or type manually"
                    />
                  </div>
                </div>

                <div className="form-group mb12">
                  <label>⚡ Electricity Rate (₹ per unit)</label>
                  <input
                    type="number"
                    name="electricityRate"
                    value={editForm.electricityRate}
                    onChange={handleEditChange}
                    placeholder="e.g. 8.50"
                    step="0.01"
                    min={0}
                  />
                  <div className="input-hint">
                    Default for all units (can be overridden per unit)
                  </div>
                </div>

                <div className="form-group mb12">
                  <label>Total Units</label>
                  <input
                    type="number"
                    name="totalUnits"
                    value={editForm.totalUnits}
                    onChange={handleEditChange}
                    placeholder="e.g. 10"
                    min={0}
                  />
                </div>

                <div className="form-group mb12">
                  <label>📍 Location (Google Map Location)</label>
                  <input
                    type="text"
                    name="location"
                    value={editForm.location}
                    onChange={handleEditChange}
                    placeholder="Paste Google Maps link"
                  />
                </div>

                <div className="form-group mb12">
                  <label>Notes / Instructions</label>
                  <textarea
                    name="notes"
                    value={editForm.notes}
                    onChange={handleEditChange}
                    style={{ minHeight: 70 }}
                    placeholder="General notes for tenants…"
                  />
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    name="isActive"
                    value={editForm.isActive}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        isActive: e.target.value === "true",
                      }))
                    }
                  >
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </select>
                </div>
              </div>

              {/* Right — Amenities */}
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                    letterSpacing: ".06em",
                    marginBottom: 10,
                  }}
                >
                  Amenities
                </div>
                {/* <div
                  style={{ maxHeight: 480, overflowY: "auto", paddingRight: 4 }}
                > */}
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 4 }}
                >
                  {isSocietyAmenitiesData.map((amenity) => (
                    <label
                      key={amenity.amenityId}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                        fontSize: 12,
                        cursor: "pointer",
                        padding: "6px 10px",
                        border: `0.5px solid ${editForm.amenityIds?.includes(amenity.amenityId) ? "var(--blue)" : "var(--border-light)"}`,
                        borderRadius: "var(--radius-md)",
                        background: editForm.amenityIds?.includes(
                          amenity.amenityId,
                        )
                          ? "var(--blue-light)"
                          : "var(--bg-primary)",
                        userSelect: "none",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          editForm.amenityIds?.includes(amenity.amenityId) ||
                          false
                        }
                        onChange={() =>
                          handleEditAmenityToggle(amenity.amenityId)
                        }
                        style={{ marginTop: 2, flexShrink: 0 }}
                      />
                      <span style={{ fontSize: 16, flexShrink: 0 }}>
                        {amenity.icon}
                      </span>
                      <span>
                        <span style={{ fontWeight: 600 }}>
                          {amenity.amenityName}
                        </span>
                        {amenity.description && amenity.showMode !== "name" && (
                          <div
                            style={{
                              fontSize: 10,
                              color: "var(--text-tertiary)",
                              marginTop: 1,
                              lineHeight: "1.4",
                            }}
                          >
                            {amenity.description}
                          </div>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
                {/* </div> */}

                {/* Map preview */}
                {(() => {
                  const embedUrl = getEmbedUrl(editForm.location);
                  return embedUrl ? (
                    <div
                      style={{
                        marginTop: 8,
                        borderRadius: "var(--radius-md)",
                        overflow: "hidden",
                        border: "0.5px solid var(--border-mid)",
                        height: 200,
                      }}
                    >
                      <iframe
                        src={embedUrl}
                        width="100%"
                        height="200"
                        style={{ border: 0, display: "block" }}
                        allowFullScreen
                        loading="lazy"
                        title="Map Preview"
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        marginTop: 8,
                        borderRadius: "var(--radius-md)",
                        background: "var(--bg-secondary)",
                        height: 100,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "var(--text-tertiary)",
                        fontSize: 12,
                        border: "0.5px solid var(--border-mid)",
                      }}
                    >
                      🗺️ Paste a Google Maps link above to preview map here
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeEditModal}
                disabled={editLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdateSociety}
                disabled={editLoading}
              >
                {editLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
